/**
 * workbench-helpers — WorkbenchChat / WorkbenchSlicePreview 공용 순수 로직.
 *
 * DOM 의존 없이 vitest 에서 `.test.ts` 로 커버할 수 있는 함수들만 여기에 둔다.
 * React 컴포넌트 구현은 WorkbenchChat.tsx / WorkbenchSlicePreview.tsx 를 참조.
 */

// ─── 도구 이름 한국어 레이블 ────────────────────────────────────────────────

/** 도구 이름 → 한국어 레이블 매핑. */
const TOOL_LABELS: Readonly<Record<string, string>> = {
	search_blocks: "블럭 검색",
	search_packs: "팩 검색",
	suggest_slice: "슬라이스 제안",
	save_slice_to_service: "서비스에 슬라이스 저장"
}

/**
 * toolLabel — 도구 이름을 한국어 레이블로 변환.
 * 알 수 없는 이름은 그대로 반환.
 */
export function toolLabel(name: string): string {
	return TOOL_LABELS[name] ?? name
}

// ─── 도구 output 요약 ─────────────────────────────────────────────────────

/**
 * summarizeValue — 도구 input/output 을 사람이 읽기 쉬운 짧은 요약으로 변환.
 * 기본 최대 길이: 120자. 초과 시 "…" 으로 잘라낸다.
 */
export function summarizeValue(value: unknown, maxLen = 120): string {
	if (value === undefined || value === null) return "—"
	try {
		const str = typeof value === "string" ? value : JSON.stringify(value, null, 0)
		return str.length > maxLen ? `${str.slice(0, maxLen)}…` : str
	} catch {
		return String(value)
	}
}

// ─── 도구 결과 항목 목록 추출 ───────────────────────────────────────────

/**
 * extractItemList — `{ <listKey>: [{...}, ...] }` 구조에서 항목 배열을 추출.
 *
 * - search_blocks: `{ blocks: [{id, displayName, ...}] }` → `extractItemList(result, "blocks", ["id", "displayName"])`
 * - search_packs: `{ packs: [{id, displayName, icon, ...}] }` → `extractItemList(result, "packs", ["id", "displayName", "icon"])`
 *
 * 동작 규칙:
 * - listKey 가 가리키는 값이 배열이 아니면 fallback 으로 `extractStringArray` 의 id 만
 *   채운 항목 (displayName/icon 등 부가 필드는 undefined) 을 반환한다 (단, fields 에 "id" 포함 시).
 * - 항목이 객체면 fields 각각을 string 으로 검사 후 매핑. id 만 fallback 으로 `String(...)` 처리.
 * - id 가 누락된 경우 `String(item.id ?? "")` 로 강제 — 호출처에서 빈 id 필터 책임.
 *
 * @param result 도구 결과 (unknown)
 * @param listKey 항목 배열을 담은 키 (예: "blocks", "packs")
 * @param fields 추출할 string 필드 목록 (예: ["id", "displayName", "icon"])
 * @returns 항목 객체 배열 — `{ [field]: string | undefined }` 형식
 */
export function extractItemList<F extends string>(
	result: unknown,
	listKey: string,
	fields: ReadonlyArray<F>
): ReadonlyArray<{ [K in F]: K extends "id" ? string : string | undefined }> {
	type Item = { [K in F]: K extends "id" ? string : string | undefined }
	if (!result || typeof result !== "object") return []
	const obj = result as Record<string, unknown>
	const list = obj[listKey]
	if (Array.isArray(list)) {
		return (list as unknown[]).map((entry): Item => {
			if (entry && typeof entry === "object") {
				const e = entry as Record<string, unknown>
				const item = {} as Record<string, string | undefined>
				for (const f of fields) {
					if (f === "id") {
						item[f] = typeof e.id === "string" ? e.id : String(e.id ?? "")
					} else {
						item[f] = typeof e[f] === "string" ? (e[f] as string) : undefined
					}
				}
				return item as Item
			}
			// primitive 항목 → id 만 String 변환, 나머지 필드는 undefined
			const item = {} as Record<string, string | undefined>
			for (const f of fields) {
				item[f] = f === "id" ? String(entry) : undefined
			}
			return item as Item
		})
	}
	// listKey 미스 → extractStringArray fallback (id 가 fields 에 있을 때만 의미)
	if (!fields.includes("id" as F)) return []
	const ids = extractStringArray(result)
	return ids.map((id): Item => {
		const item = {} as Record<string, string | undefined>
		for (const f of fields) {
			item[f] = f === "id" ? id : undefined
		}
		return item as Item
	})
}

// ─── string[] 추출 ────────────────────────────────────────────────────────

/**
 * extractStringArray — output 에서 string[] 를 추출 (search_blocks 등).
 *
 * 지원 형태:
 * - `string[]` 직접 배열
 * - `{ blocks: [{id: string, ...}, ...] }` — id 추출
 * - `{ blockIds: string[] }` / `{ ids: string[] }` / `{ results: string[] }`
 */
export function extractStringArray(value: unknown): ReadonlyArray<string> {
	if (Array.isArray(value)) {
		return value.filter((v): v is string => typeof v === "string")
	}
	if (value !== null && typeof value === "object") {
		const obj = value as Record<string, unknown>
		// search_blocks 응답: { blocks: [{id, displayName, ...}, ...] } — 블럭 id 추출
		if (Array.isArray(obj.blocks)) {
			return (obj.blocks as unknown[])
				.map((b) => (b !== null && typeof b === "object" && "id" in b ? (b as { id: unknown }).id : null))
				.filter((id): id is string => typeof id === "string")
		}
		// 단순 { blockIds: string[] } / { ids: ... } / { results: ... } 형태 대응
		for (const key of ["blockIds", "ids", "results"]) {
			if (Array.isArray(obj[key])) {
				return (obj[key] as unknown[]).filter((v): v is string => typeof v === "string")
			}
		}
	}
	return []
}

// ─── AdminApiError 코드 → 사용자 친화 메시지 ────────────────────────────

/**
 * workbenchErrorMessage — AdminApiError code 를 사용자 친화적 메시지로 변환.
 */
export function workbenchErrorMessage(code: string, raw: string): string {
	switch (code) {
		case "rate_limited":
			return "요청 속도가 너무 빠릅니다. 잠시 후 다시 시도해 주세요."
		case "unauthorized":
			return "로그인 세션이 만료됐습니다. 다시 로그인해 주세요."
		case "upstream_error":
			return "AI 게이트웨이 호출이 실패했습니다. 잠시 후 다시 시도해 주세요."
		default:
			return raw
	}
}
